<template>
    <div id="main">
        <div class="page">
            <div class="alert-wrapper">
                <alert-container :messages="msgs" />
            </div>

            <div class="vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">{{ $t('justificativa-nao-venda') }}</h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a>{{ $t('vendas') }}</a>
                                </li>
                                <li>
                                    <a>{{ $t('relatorios') }}</a>
                                </li>
                                <li>
                                    <a href="#" aria-current="page">{{
                                        $t('justificativa-nao-venda')
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>

                <div class="search-and-filter">
                    <div class="columns is-variables is-desktop">
                        <div class="column is-3">
                            <vss-select
                                ref="motivoNaoVendaInput"
                                v-model="motivosNaoVendasSelected"
                                label="label"
                                title="filtro-motivo-nao-venda"
                                :options="motivosNaoVendasOptions"
                                @search="loadMotivosNaoVendas"
                                searchable
                                :multiple="true"
                                :loading="motivosNaoVendasLoading"
                                :height="40"
                                noClearfix
                            />
                        </div>
                        <div class="column is-3">
                            <vss-select
                                ref="clienteFilterInput"
                                class="smaller"
                                v-model="clienteOptionSelected"
                                label="label"
                                title="cliente"
                                :options="clienteOptions"
                                @search="loadClientes"
                                searchable
                                :multiple="true"
                                :loading="clienteLoading"
                                :height="40"
                                noClearfix
                            />
                        </div>
                        <div class="column is-3">
                            <verified-date-picker
                                dateType="startDate"
                                v-model="datainicial"
                                :endDate="datafinal"
                                title="data-inicio"
                                :showClean="false"
                            />
                        </div>
                        <div class="column is-3">
                            <div class="is-mobile mt-2-custom">
                                <b-button
                                    class="float-right"
                                    type="is-primary"
                                    icon-left="map"
                                    @click="
                                        openLocationEditor(
                                            tableData,
                                            -1,
                                            tableData != null && tableData.length != 0
                                        )
                                    "
                                    :disabled="!(tableData != null && tableData.length != 0)"
                                    v-tippy 
                                    :title="$t('mapa')"
                                />
                                <b-button
                                    class="mr-2 float-right"
                                    type="is-primary"
                                    icon-right="google-spreadsheet"
                                    @click="getReportCSV()"
                                    :disabled="!(tableData != null && tableData.length != 0)"
                                    v-tippy 
                                    :title="$t('msg-tooltip-csv')"
                                />
                                <b-button
                                    class="mr-2 float-right"
                                    type="is-primary"
                                    icon-right="filter"
                                    @click="showFiltros = !showFiltros"
                                    v-tippy 
                                    :title="$t('filtros')"
                                />
                                <b-button
                                    class="mr-2 float-right"
                                    type="is-success"
                                    icon-right="magnify"
                                    @click="carregarDados()"
                                    v-tippy 
                                    :title="$t('pesquisar')"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <section class="registry-wrapper mt-4">
                    <!-- Tabela -->
                    <container-loader v-if="isLoading && !$store.getters.isLoading" />
                    <empty-table-message class="mt-3" v-if="isEmpty" />
                    <b-table
                        v-else
                        :data="tableData"
                        class="data-table text-small"
                        @sort="sortTable"
                        backend-sorting
                    >
                        <!-- Dados -->
                        <b-table-column
                            v-for="(field, index) in tableFields"
                            :key="index"
                            :label="$t(field.label)"
                            :visible="field.visible"
                            :numeric="field.numeric"
                            :sortable="field.sortable"
                            :field="field.key"
                            v-slot="props"
                        >
                            <div v-if="field.key == 'local-nao-venda'">
                                <a
                                    v-if="hasApiKey"
                                    @click="
                                        openLocationEditor(
                                            props.row,
                                            props.index,
                                            containsLocation(props.row)
                                        )
                                    "
                                >
                                    <b-icon
                                        type="is-primary"
                                        icon="map-marker"
                                        class="map-marker"
                                        :class="{ disabled: !containsLocation(props.row) }"
                                    />
                                </a>
                                <b-icon
                                    v-else
                                    type="is-primary"
                                    icon="map-marker"
                                    style="opacity: 0.4"
                                />
                            </div>
                            <div v-else-if="field.key == 'rsocialCliente'">
                                <a @click="showJustificativaDetalheModal(props.row.idNaoVenda)">
                                    {{ props.row.rsocialCliente }}
                                </a>
                            </div>
                            <div v-else-if="field.key == 'fotos'">
                                {{ props.row.fotos.length }}
                            </div>
                            <div v-else-if="field.key == 'dataNaoVenda'">
                                {{ props.row[field.key] | moment('L') }}
                            </div>
                            <div v-else-if="field.key == 'empresa'">
                                {{ props.row.empresa.rsocial }}
                            </div>
                            <div v-else class="table-data numeric">
                                {{ props.row[field.key] }}
                            </div>
                        </b-table-column>
                    </b-table>
                </section>

                <b-modal :active.sync="isShowingLocation" @close="onCancelLocationEditor()">
                    <div class="modal-card modal-default">
                        <section class="modal-card-body">
                            <div class="listing-header">
                                <div class="title-breadcrumb">
                                    <h1 class="title">{{ $t('localizacao') }}</h1>

                                    <div
                                        class="columns is-vcentered is-multiline is-mobile is-gapless mb-0"
                                    >
                                        <div class="column is-narrow">
                                            <b-icon
                                                icon="home"
                                                class="cliente-icon icon"
                                                :style="{ color: primary_color }"
                                            />
                                        </div>
                                        <div class="column">
                                            <label>{{
                                                $t('gps-cliente') +
                                                (checkPosition('cliente')
                                                    ? ' (' +
                                                      $t('sem-localizacao').toLowerCase() +
                                                      ')'
                                                    : '')
                                            }}</label>
                                        </div>
                                    </div>

                                    <div
                                        class="columns is-vcentered is-multiline is-mobile is-gapless mb-0"
                                    >
                                        <div class="column is-narrow">
                                            <b-icon
                                                icon="map-marker"
                                                class="nao-venda-icon icon"
                                                :style="{ color: secondary_color }"
                                            />
                                        </div>
                                        <div class="column">
                                            <label>{{
                                                $t('gps-geracao-nao-venda') +
                                                (checkPosition('naovenda')
                                                    ? ' (' +
                                                      $t('sem-localizacao').toLowerCase() +
                                                      ')'
                                                    : '')
                                            }}</label>
                                        </div>
                                    </div>

                                    <div
                                        class="columns is-vcentered is-multiline is-mobile is-gapless mb-0"
                                    >
                                        <div class="column is-narrow">
                                            <b-icon icon="numeric" class="icon cliente-icon" />
                                        </div>
                                        <div class="column">
                                            <label>{{ $t('icon-number') }}</label>
                                        </div>
                                    </div>
                                </div>
                                <div class="header-buttons">
                                    <button
                                        class="delete-btn"
                                        type="button"
                                        @click="showMapLine = !showMapLine"
                                    >
                                        {{
                                            showMapLine
                                                ? $t('esconder-linhas')
                                                : $t('mostrar-linhas')
                                        }}
                                    </button>
                                </div>
                            </div>

                            <gmap-map
                                ref="mapRef"
                                class="map-el"
                                :center="center"
                                :zoom="zoomLevel"
                                :options="mapOptions"
                                style="margin-top: 10px"
                                @tilesloaded="centerOnRoute"
                            >
                                <gmap-marker
                                    v-for="(pin, index) in selectedPositions"
                                    :key="index"
                                    id="marker"
                                    :position="pin"
                                    :icon="pin.origin == 0 ? pinIconCliente : pinIconNaoVenda"
                                    :draggable="false"
                                    :visible="isShowingMapMarker && canShowPin(pin)"
                                    :label="{
                                        text: pin.label != null ? pin.label : index,
                                        color: '#fff',
                                        fontWeight: '600',
                                        fontSize: '11px',
                                        fontFamily: 'Montserrat,sans-serif',
                                    }"
                                    @click="openInfo(pin)"
                                />
                                <gmap-polyline
                                    v-for="(line, ind) in pathLineCliente"
                                    :key="ind + '-line'"
                                    :path="line"
                                    :options="{
                                        strokeColor: `#000`,
                                        strokeOpacity: 1 * showMapLine * canShowLine(line),
                                        icons: [
                                            {
                                                // icon: { path: 1 /*google.maps.SymbolPath.FORWARD_OPEN_ARROW*/ },
                                                icon: {
                                                    path: 'M 2,3 0,0 -2,3',
                                                    strokeOpacity:
                                                        1 * showMapLine * canShowLine(line),
                                                    scale: 3,
                                                },
                                                offset: '40%',
                                                repeat: '50px',
                                            },
                                        ],
                                        zIndex: 10,
                                    }"
                                    :editable="false"
                                />
                                <!-- TOOLTIP -->
                                <gmap-info-window
                                    ref="tooltipWindow"
                                    :position="tooltipWindow.position"
                                    :opened="tooltipWindow.open"
                                    :options="tooltipWindow.options"
                                    @closeclick="tooltipWindow.open = false"
                                >
                                    <div
                                        class="info-window"
                                        v-if="
                                            tableData != null &&
                                            tableData.length > 0 &&
                                            tooltipWindow.origin == 0
                                        "
                                    >
                                        <p class="info-window-nome">
                                            {{ tableData[tooltipWindow.itempos].rsocialCliente }}
                                        </p>
                                    </div>
                                    <div
                                        class="info-window"
                                        v-else-if="
                                            tableData != null &&
                                            tableData.length > 0 &&
                                            tooltipWindow.origin == 1
                                        "
                                    >
                                        <p class="info-window-nome">
                                            {{ tableData[tooltipWindow.itempos].rsocialCliente }}
                                        </p>
                                        <div class="info-detail">
                                            <span class="has-text-weight-bold"
                                                >{{ $t('vendedor') }}:</span
                                            >
                                            <span class="has-text-right">{{
                                                tableData[tooltipWindow.itempos].nomeVendedor
                                                    ? tableData[tooltipWindow.itempos].nomeVendedor
                                                    : '-'
                                            }}</span>
                                        </div>
                                        <div class="info-detail">
                                            <span class="has-text-weight-bold"
                                                >{{ $t('data') }}:</span
                                            >
                                            <span class="has-text-right">{{
                                                tableData[tooltipWindow.itempos].dataNaoVenda
                                                    ? tableData[tooltipWindow.itempos].dataNaoVenda
                                                    : '-'
                                            }}</span>
                                        </div>
                                        <div class="info-detail">
                                            <span class="has-text-weight-bold"
                                                >{{ $t('contato') }}:</span
                                            >
                                            <span class="has-text-right">{{
                                                tableData[tooltipWindow.itempos].contato
                                                    ? tableData[tooltipWindow.itempos].contato
                                                    : '-'
                                            }}</span>
                                        </div>
                                        <div class="info-detail">
                                            <span class="has-text-weight-bold"
                                                >{{ $t('observacao') }}:</span
                                            >
                                            <span class="has-text-right">{{
                                                tableData[tooltipWindow.itempos].observacao
                                                    ? tableData[tooltipWindow.itempos].observacao
                                                    : '-'
                                            }}</span>
                                        </div>
                                        <div class="info-detail">
                                            <span class="has-text-weight-bold"
                                                >{{ $t('motivo-nao-venda') }}:</span
                                            >
                                            <span class="has-text-right">{{
                                                tableData[tooltipWindow.itempos].motivoNaoVenda
                                                    ? tableData[tooltipWindow.itempos]
                                                          .motivoNaoVenda
                                                    : '-'
                                            }}</span>
                                        </div>
                                    </div>
                                </gmap-info-window>
                            </gmap-map>
                        </section>
                    </div>
                </b-modal>
                <b-modal :active.sync="isShowingJustificativaDetalhe">
                    <JustificativaNaoVendaDetalhada :idnaovenda="idNaoVendaDetalhe">
                    </JustificativaNaoVendaDetalhada>
                </b-modal>

                <PaginationViewer
                    class="mb-0"
                    :resetPagination="pagination.resetPagination"
                    :totalItems="pagination.totalRows"
                    :perPageOptions="pagination.perPageOptions"
                    :optionStarted="pagination.optionStarted"
                    @change="updatePagination"
                />
            </div>
        </div>

        <div
            ref="listaFiltros"
            class="filtros-right"
            :class="{ 'hide-filtros-right': !showFiltros }"
            v-show="!isLoading"
        >
            <div class="filtros-container">
                <div class="columns is-multiline">
                    <div class="column is-12">
                        <verified-date-picker
                            dateType="endDate"
                            v-model="datafinal"
                            :startDate="datainicial"
                            title="data-fim"
                            :showClean="false"
                        />
                    </div>

                    <div class="column is-12">
                        <vss-select
                            title="fotos"
                            ref="fotosInput"
                            label="label"
                            :options="opts"
                            v-model="fotos"
                            :height="40"
                            noClearfix
                            mlLabels
                        />
                    </div>

                    <div class="column is-12">
                        <vss-select
                            v-if="hasEmpresa"
                            title="empresa"
                            ref="empresaFilterInput"
                            v-model="empresaOptionSelected"
                            :options="empresaOptions"
                            :loading="empresaLoading"
                            label="rsocial"
                            :height="40"
                            :ml-labels-field="'traduzir'"
                            @search="loadEmpresas"
                            searchable
                            multiple
                            noClearfix
                        />
                    </div>

                    <div class="column is-12">
                        <vss-select
                            v-if="hasVendedor"
                            title="vendedor"
                            ref="vendedorFilterInput"
                            v-model="vendedorOptionSelected"
                            :options="vendedorOptions"
                            :loading="vendedorLoading"
                            @search="loadVendedores"
                            searchable
                            :height="40"
                            multiple
                            noClearfix
                            clearable
                        />
                    </div>
                </div>
            </div>
            <div class="filtros-footer">
                <div class="columns is-mobile">
                    <div class="column">
                        <b-button
                            type="is-warning is-silver"
                            icon-right="close"
                            @click="showFiltros = !showFiltros"
                            v-tippy 
                            :title="$t('fechar')"
                        />
                    </div>
                    <div class="column">
                        <b-button
                            type="is-warning is-silver"
                            icon-right="backspace"
                            @click="onCleanFilters()"
                            class="float-right"
                            >{{ $t('limpar') }}</b-button
                        >
                    </div>
                    <div class="column">
                        <b-button
                            type="is-primary"
                            icon-right="filter"
                            @click="timeoutCarregarDados()"
                            class="float-right"
                            >{{ $t('filtrar') }}</b-button
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FiltroRelatorioService from '@/services/FiltroRelatorioService';
import ColaboradorService from '@/services/ColaboradorService';
import VendasRelJustificativaNaoVendaService from '@/services/VendasRelJustificativaNaoVendaService';
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import Message from '@/models/Message';
import { palette as paleta } from '@/functions/color/Palette';
import JustificativaNaoVendaDetalhada from '@/components/vendas/relatorios/JustificativaNaoVendaDetalhada';

export default {
    components: {
        ContainerLoader,
        JustificativaNaoVendaDetalhada,
    },
    data() {
        return {
            datasStart: {
                datainicial: (() => {
                    let trintaDiasAtras = new Date();
                    trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 31);
                    return trintaDiasAtras;
                })(),
                datafinal: new Date(),
            },
            datainicial: null,
            datafinal: null,
            isShowingJustificativaDetalhe: false,
            idNaoVendaDetalhe: 0,
            dataService: null,
            filtroService: null,
            opts: [
                { label: 'todos', id: 0 },
                { label: 'com-fotos', id: 1 },
                { label: 'sem-fotos', id: 2 },
            ],
            fotos: { label: 'todos', id: 0 },
            hasEmpresa: false,
            hasVendedor: false,
            showSecondaryFilters: false,
            relatorioNaoVendaTable: null,
            clienteOptions: [],
            clienteOptionSelected: null,
            clienteLoading: false,
            empresaOptions: [],
            empresaOptionSelected: [],
            empresaLoading: false,
            vendedorOptions: [],
            vendedorOptionSelected: [],
            vendedorLoading: false,
            motivosNaoVendasOptions: [],
            motivosNaoVendasSelected: null,
            motivosNaoVendasLoading: false,
            tableData: [],
            tableFields: [
                { key: 'rsocialCliente', label: 'cliente', sortable: true, visible: true },
                {
                    key: 'nomeVendedor',
                    label: 'vendedor',
                    sortable: true,
                    numeric: false,
                    visible: false,
                },
                {
                    key: 'empresa',
                    label: 'empresa',
                    sortable: true,
                    numeric: false,
                    visible: false,
                },
                {
                    key: 'motivoNaoVenda',
                    label: 'motivo-nao-venda',
                    sortable: true,
                    numeric: false,
                    visible: true,
                },
                {
                    key: 'dataNaoVenda',
                    label: 'data',
                    sortable: true,
                    numeric: false,
                    visible: true,
                },
                {
                    key: 'observacao',
                    label: 'observacao',
                    sortable: true,
                    numeric: false,
                    visible: true,
                },
                {
                    key: 'local-nao-venda',
                    label: 'local',
                    sortable: false,
                    numeric: true,
                    visible: true,
                },
                { key: 'fotos', label: 'fotos', sortable: false, numeric: true, visible: true },
            ],
            localeOpts: {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            },
            isExporting: false,
            isEmpty: false,
            msgs: [],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            column: 'cliente',
            order: 'desc',
            onlyOnce: true,

            // Mapa
            primary_color: this.$store.state.partnerSystemHasLayout.primary_color,
            secondary_color: paleta.getChartColors()[paleta.getChartColors().length - 1],
            isShowingLocation: false,
            selectedPositions: [
                {
                    lat: 0,
                    lng: 0,
                    origin: 0, // Campo Origin define se o pin é do cliente (0), do local onde a nao venda foi gerada (1) ou do checkin (2).
                    label: '0', // Campo que define a label dos pins. Deve ser igual em todos os pins do cliente e do vendedor
                    itempos: 0, // Mesmo valor que o label, mas como inteiro.
                },
                {
                    lat: 0,
                    lng: 0,
                    origin: 1, // Campo Origin define se o pin é do cliente (0), ou do local onde a nao venda foi gerada (1) ou do checkin (2).
                    label: '0', // Campo que define a label dos pins. Deve ser igual em todos os pins do cliente e do vendedor
                    itempos: 0, // Mesmo valor que o label, mas como inteiro.
                },
            ],
            center: { lat: -15, lng: -50 },
            zoomLevel: 4,
            mapOptions: {
                mapTypeControl: false,
                streetViewControl: false,
                scaleControl: false,
                zoomControl: false,
                fullscreenControl: true,
                fullscreenControlOptions: {
                    position: 7, //google.maps.ControlPosition.RIGHT_TOP
                },
            },
            tooltipWindow: {
                position: { lat: 0, lng: 0 },
                open: false,
                itempos: 0,
                options: {
                    pixelOffset: { width: 10, height: -25 },
                },
                origin: 0,
            },
            pinIconCliente: {
                path:
                    'M 12 2 A 1 1 0 0 0 11.289062 2.296875 L 1.203125 11.097656 A 0.5 0.5 0 0 0 1 11.5 A 0.5 0.5 0 0 0 1.5 12 L 4 12 L 4 20 C 4 20.552 4.448 21 5 21 L 9 21 C 9.552 21 10 20.552 10 20 L 10 14 L 14 14 L 14 20 C 14 20.552 14.448 21 15 21 L 19 21 C 19.552 21 20 20.552 20 20 L 20 12 L 22.5 12 A 0.5 0.5 0 0 0 23 11.5 A 0.5 0.5 0 0 0 22.796875 11.097656 L 12.716797 2.3027344 A 1 1 0 0 0 12.710938 2.296875 A 1 1 0 0 0 12 2 z',
                //ICONE CASA
                fillColor: this.$store.state.partnerSystemHasLayout.primary_color,
                scale: 1.5,
                fillOpacity: 1,
                strokeWeight: 0,
                anchor: { x: 6, y: 15 },
                labelOrigin: { x: 11, y: 9 },
            },
            pinIconNaoVenda: {
                path:
                    'M 25 1 C 16.179688 1 9 8.179688 9 17 C 9 31.113281 23.628906 47.945313 24.25 48.65625 C 24.441406 48.875 24.710938 49 25 49 C 25.308594 48.980469 25.558594 48.875 25.75 48.65625 C 26.371094 47.933594 41 30.8125 41 17 C 41 8.179688 33.820313 1 25 1 Z M 25 12 C 28.3125 12 31 14.6875 31 18 C 31 21.3125 28.3125 24 25 24 C 21.6875 24 19 21.3125 19 18 C 19 14.6875 21.6875 12 25 12 Z',
                fillColor: paleta.getChartColors()[paleta.getChartColors().length - 1],
                scale: 0.8,
                fillOpacity: 1,
                strokeWeight: 0,
                anchor: { x: 10, y: 25 },
                labelOrigin: { x: 24, y: 35 },
            },
            showMapLine: true,
            pathLineCliente: [],
            isShowingMapMarker: true,
            loadedOnce: false,
            permissao: {},
            isLoading: false,
            showFiltros: false,
            hasApiKey: false,
        };
    },
    created() {
        this.hasApiKey = this.checkApiKey();
        this.setStartDates();
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];

        this.permissao = this.hasPermissionAccess(
            'vendas',
            'relatorio-analitico',
            'relatorio-vendas-justificativa-nao-venda'
        );

        !this.permissao.read &&
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'justificativa-nao-venda' },
            });

        this.dataService = new VendasRelJustificativaNaoVendaService(this.$http, this.$router);
        this.filtroService = new FiltroRelatorioService(this.$http, this.$router);
        this.colaboradorService = new ColaboradorService(this.$http, this.$router);

        window.addEventListener('click', this.clickEvt);

        this.loadEmpresas();
        this.loadMotivosNaoVendas('');
        this.loadVendedores('');
        this.loadClientes('');

        this.carregarDados();
    },
    methods: {
        checkApiKey() {
            if (!this.$store.state.user.gmapsApiKey) {
                /* Admin: Mostra a opção de ir na config pra inserir a chave. */
                this.$root.confirm({
                    message: this.$t('msg-justificativanaovenda-no-key'),
                    onConfirm: () => {
                        this.$router.push({ name: 'configuracao-mapas' });
                    },
                });
                return false;
            } else {
                return true;
            }
        },
        setStartDates() {
            this.datainicial = this.datasStart.datainicial;
            this.datafinal = this.datasStart.datafinal;
        },
        checkDates() {
            let temp = this.datainicial == null || this.datafinal == null ? false : true;
            if (!temp) {
                this.showMessage('error', 'datas-obrigatorias');
            }
            return temp;
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            if (this.checkDates() && !this.isExporting) {
                if (currentPage == 1 && this.pagination.currentPage != 1)
                    this.pagination.resetPagination = true;
                this.isLoading = true;

                this.dataService
                    .getJustificationNoSales(this.getFilter(currentPage))
                    .then((r) => {
                        this.pagination.totalRows = r.totalElements;
                        this.tableData = r.content;
                        this.isEmpty = r.content.length == 0 ? true : false;
                    })
                    .catch((e) => {
                        console.error('Erro ao trazer dados das nao vendas.', e);
                        this.showMessage('error', 'msg-erro-relatorio');
                        this.isEmpty = true;
                    })
                    .finally(() => {
                        this.pagination.resetPagination = false;
                        this.isLoading = false;
                    });
                this.showSecondaryFilters = false;
            }
        },
        getReportCSV() {
            if (this.checkDates() && !this.isExporting) {
                this.isLoading = true;
                this.isExporting = true;
                let filtros = this.getFilter();
                this.dataService
                    .setStartCSVReport(filtros)
                    .then((r) => {
                        filtros.exportacao = r.content;
                        this.$root.showMessage('success', 'msg-exp-solicitada');

                        this.dataService
                            .getCSVReport(filtros)
                            .then(console.log)
                            .catch(console.error);
                    })
                    .catch((e) => {
                        console.error('Erro ao trazer csv das nao vendas.', e);
                        this.showMessage('error', 'msg-erro-relatorio');
                    })
                    .finally(() => {
                        this.isLoading = false;
                        this.isExporting = false;
                    });
            }
        },
        getFilter() {
            let filtros = {
                ini: this.datainicial ? this.$moment(this.datainicial).format('DD/MM/YYYY') : null,
                fim: this.datafinal ? this.$moment(this.datafinal).format('DD/MM/YYYY') : null,
                motivosNaoVenda:
                    this.motivosNaoVendasSelected != null
                        ? this.motivosNaoVendasSelected.map((x) => x.id)
                        : [],
                vendedores:
                    this.hasVendedor && this.vendedorOptionSelected != null
                        ? this.vendedorOptionSelected.map((x) => x.id)
                        : [],
                empresas:
                    this.hasEmpresa && this.empresaOptionSelected != null
                        ? this.empresaOptionSelected.map((x) => x.id)
                        : [],
                hasFotos: this.fotos != null ? this.fotos.id : 0,
                clientes:
                    this.clienteOptionSelected != null
                        ? this.clienteOptionSelected.map((a) => a.id)
                        : [],
                pageNumber: this.pagination.currentPage - 1,
                limit: this.pagination.perPage,
                order: this.order,
                column: this.column,
                usuario: {
                    idusuario: this.$store.state.user.idusuario,
                    idusuarioerp: this.$store.state.user.idusuarioerp,
                },
                exportacao: {},
            };
            return filtros;
        },
        downloadCSV(csv) {
            var filename, link, blob, url;

            if (csv == null) return;

            blob = new Blob(['\ufeff', csv], {
                encoding: 'UTF-8',
                type: 'data:text/csv;charset=utf-8;',
            });
            url = URL.createObjectURL(blob);

            filename = 'justificativanaovenda.csv';

            link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        },
        loadMotivosNaoVendas(q) {
            this.motivosNaoVendasLoading = true;

            this.filtroService
                .findAllMotivosNaoVendasFilter(0, 20, q)
                .then((r) => {
                    if (r.status != 200) {
                        console.error(
                            `Erro com status ${r.status} ao realizar pesquisa por motivos não venda. Termo: [${q}].`
                        );
                        this.motivosNaoVendasOptions = [];
                    } else {
                        this.motivosNaoVendasOptions = [];
                        if (
                            this.motivosNaoVendasSelected != null &&
                            this.motivosNaoVendasSelected.length > 0
                        ) {
                            this.motivosNaoVendasOptions = this.motivosNaoVendasOptions.concat(
                                this.motivosNaoVendasSelected
                            );
                        }
                        if (this.motivosNaoVendasOptions.length == 0) {
                            this.motivosNaoVendasOptions = this.motivosNaoVendasOptions.concat(
                                r.content.map((v) => {
                                    return {
                                        id: v.id,
                                        label: v.descricao.toLocaleUpperCase(),
                                    };
                                })
                            );
                        } else {
                            this.motivosNaoVendasOptions = this.motivosNaoVendasOptions.concat(
                                r.content
                                    .filter((f) => {
                                        if (
                                            this.motivosNaoVendasOptions.findIndex(
                                                (ind) => ind.id == f.id
                                            ) == -1
                                        ) {
                                            return f;
                                        }
                                    })
                                    .map((v, index) => {
                                        return {
                                            id: v.id,
                                            label: v.descricao.toLocaleUpperCase(),
                                        };
                                    })
                            );
                        }
                    }
                })
                .catch((e) => {
                    console.error(
                        `Erro ao realizar pesquisa por motivos não venda. Termo: [${q}].`,
                        e
                    );
                    this.motivosNaoVendasOptions = [];
                })
                .finally(() => {
                    this.motivosNaoVendasLoading = false;
                });
        },
        loadVendedores(q) {
            this.vendedorLoading = true;

            this.colaboradorService
                .findListAssociated(q)
                .then((r) => {
                    if (r.status != 200) {
                        console.error(
                            `Erro com status ${r.status} ao realizar pesquisa por vendedores. Termo: [${q}].`
                        );
                        this.vendedorOptions = [];
                    } else {
                        if (r.length > 1) {
                            this.hasVendedor = true;
                            this.tableFields[1].visible = true;
                        }
                        this.vendedorOptions = r.content.map((v) => {
                            return {
                                id: v.id,
                                label: v.nome.toLocaleUpperCase(),
                            };
                        });
                    }
                })
                .catch((e) => {
                    console.error(`Erro ao realizar pesquisa por vendedores. Termo: [${q}].`, e);
                    this.vendedorOptions = [];
                })
                .finally(() => {
                    this.vendedorLoading = false;
                    if (this.vendedorOptions.length > 1) {
                        this.hasVendedor = true;
                        this.tableFields[1].visible = true;
                    }
                });
        },
        loadEmpresas(search = '') {
            this.empresaLoading = true;

            if (this.$store.state.user.admin) {
                // se for admin, mostra todas as empresas
                this.empresaOptions = this.$store.state.empresas.data.filter((e) => {
                    return e.rsocial.toLocaleUpperCase().includes(search.toLocaleUpperCase());
                });
            } else {
                // se for cliente, mostra todas as empresas relacionadas a ele
                this.empresaOptions = this.$store.state.user.empresas.filter((e) => {
                    return e.rsocial.toLocaleUpperCase().includes(search.toLocaleUpperCase());
                });
            }

            this.empresaLoading = false;

            if (this.empresaOptions.length == 0) {
                this.$root.alert({
                    message: this.$t('msg-usuario-sem-empresa'),
                    onConfirm: () => {
                        this.redirect(0);
                    },
                });
            } else if (this.empresaOptions.length > 1) {
                this.hasEmpresa = true;
                this.tableFields[2].visible = true;
            }
        },
        loadClientes(q) {
            this.clienteLoading = true;

            this.filtroService
                .findAllClienteVendas(20, q)
                .then((r) => {
                    if (r.status != 200) {
                        console.error(
                            `Erro com status ${r.status} ao realizar pesquisa por cliente. Termo: [${q}].`
                        );
                        this.clienteOptions = [];
                    } else {
                        this.clienteOptions = [];
                        if (
                            this.clienteOptionSelected != null &&
                            this.clienteOptionSelected.length > 0
                        ) {
                            this.clienteOptions = this.clienteOptions.concat(
                                this.clienteOptionSelected
                            );
                        }
                        if (this.clienteOptions.length == 0) {
                            this.clienteOptions = this.clienteOptions.concat(
                                r.content.map((v) => {
                                    return {
                                        id: v.id,
                                        label: v.rsocial.toLocaleUpperCase(),
                                    };
                                })
                            );
                        } else {
                            this.clienteOptions = this.clienteOptions.concat(
                                r.content
                                    .filter((f) => {
                                        if (
                                            this.clienteOptions.findIndex(
                                                (ind) => ind.id == f.id
                                            ) == -1
                                        ) {
                                            return f;
                                        }
                                    })
                                    .map((v, index) => {
                                        return {
                                            id: v.id,
                                            label: v.rsocial.toLocaleUpperCase(),
                                        };
                                    })
                            );
                        }
                    }
                })
                .catch((e) => {
                    console.error(`Erro ao realizar pesquisa por cliente. Termo: [${q}].`, e);
                    this.clienteOptions = [];
                })
                .finally(() => {
                    this.clienteLoading = false;
                });
        },
        onCleanFilters() {
            if (!this.isExporting) {
                this.vendedorOptionSelected = null;
                this.clienteOptionSelected = [];
                this.fotos = { label: 'todos', id: 0 };
                this.motivosNaoVendasSelected = [];
                this.empresaOptionSelected = [];
                this.setStartDates();
                this.timeoutCarregarDados();
            }
        },
        timeoutCarregarDados(time = 600) {
            this.showFiltros = false;
            setTimeout(() => {
                this.carregarDados();
            }, time);
        },
        showMessage(type, message) {
            this.msgs.push(new Message(type, message, true));
        },
        openLocationEditor(naovenda, index, enabled) {
            if (enabled) {
                let posCliente, posNaoVenda;
                this.selectedPositions = [];
                this.pathLineCliente = [];

                if (index >= 0 && naovenda != null) {
                    posCliente = {};
                    posNaoVenda = {};
                    // Campo Origin define se o pin é do cliente (0) ou do local onde a nao venda foi gerada (1).
                    // Campo Label define a o texto dos pins. Deve ser igual em ambos os pins do cliente e do vendedor
                    posCliente.lat = naovenda.latCliente;
                    posCliente.lng = naovenda.lngCliente;
                    posCliente.origin = 0;
                    posCliente.label = (
                        index +
                        1 +
                        (this.pagination.currentPage - 1) * this.pagination.perPage
                    ).toString();
                    posCliente.itempos =
                        index + (this.pagination.currentPage - 1) * this.pagination.perPage;
                    posNaoVenda.lat = naovenda.latNaoVenda;
                    posNaoVenda.lng = naovenda.lngNaoVenda;
                    posNaoVenda.origin = 1;
                    posNaoVenda.label = (
                        index +
                        1 +
                        (this.pagination.currentPage - 1) * this.pagination.perPage
                    ).toString();
                    posNaoVenda.itempos =
                        index + (this.pagination.currentPage - 1) * this.pagination.perPage;

                    this.selectedPositions.push(posCliente, posNaoVenda);
                    this.pathLineCliente.push([posCliente, posNaoVenda]);
                    this.isShowingMapMarker = this.containsLocation(naovenda);
                } else if (index == -1 && naovenda != null) {
                    naovenda.forEach((el, nvindex) => {
                        posCliente = {};
                        posNaoVenda = {};

                        posCliente.lat = el.latCliente;
                        posCliente.lng = el.lngCliente;
                        posCliente.origin = 0;
                        posCliente.label = (
                            nvindex +
                            1 +
                            (this.pagination.currentPage - 1) * this.pagination.perPage
                        ).toString();
                        posCliente.itempos =
                            nvindex + (this.pagination.currentPage - 1) * this.pagination.perPage;
                        posNaoVenda.lat = el.latNaoVenda;
                        posNaoVenda.lng = el.lngNaoVenda;
                        posNaoVenda.origin = 1;
                        posNaoVenda.label = (
                            nvindex +
                            1 +
                            (this.pagination.currentPage - 1) * this.pagination.perPage
                        ).toString();
                        posNaoVenda.itempos =
                            nvindex + (this.pagination.currentPage - 1) * this.pagination.perPage;

                        if (this.containsLocation(el)) {
                            //Só insere pins que a latitude e longitude do cliente são diferentes de null ou 0
                            this.selectedPositions.push(posCliente, posNaoVenda);
                            this.pathLineCliente.push([posCliente, posNaoVenda]);
                        }
                    });
                    this.isShowingMapMarker = true;
                }
                this.isShowingLocation = true;
            }
        },
        centerOnRoute() {
            if (!this.loadedOnce) {
                this.$refs.mapRef.$mapPromise.then((map) => {
                    let bounds = new google.maps.LatLngBounds();
                    let count = 0;

                    this.selectedPositions.forEach((r) => {
                        if (r.lat != 0 && r.lng != 0) {
                            count++;
                            bounds.extend(r);
                        }
                    });

                    if (count > 1) {
                        map.fitBounds(bounds);
                    } else {
                        map.setCenter(this.selectedPositions[0]);
                        map.setZoom(this.zoomLevel * 2);
                    }
                });
                this.loadedOnce = true;
            }
        },
        openSecondaryFilters() {
            this.showSecondaryFilters = !this.showSecondaryFilters;
        },
        onCancelLocationEditor() {
            this.isShowingLocation = false;
            this.selectedPositions = [];
            this.pathLineCliente = [];
            this.loadedOnce = false;
        },
        openInfo(evt) {
            this.tooltipWindow.position = {
                lat: evt.lat,
                lng: evt.lng,
            };
            this.tooltipWindow.origin = evt.origin;
            this.tooltipWindow.itempos = evt.itempos;
            this.tooltipWindow.open = true;
        },
        containsLocation(naovenda) {
            return (
                naovenda != null &&
                naovenda.latCliente != null &&
                naovenda.lngCliente != null &&
                naovenda.latCliente != 0 &&
                naovenda.lngCliente != 0
            );
        },
        redirect(time) {
            setTimeout(() => {
                this.$router.push({ name: 'ven-cockpit' });
            }, time * 1000);
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados(this.pagination.currentPage);
        },
        clickEvt(e) {
            let path = e.path || e.composedPath();

            if (
                this.showSecondaryFilters &&
                !path.includes(this.$refs.secondaryFilter) &&
                !path.includes(this.$refs.btnFilter)
            ) {
                this.showSecondaryFilters = false;
            }
        },
        showJustificativaDetalheModal(id) {
            this.isShowingJustificativaDetalhe = !this.isShowingJustificativaDetalhe;
            this.idNaoVendaDetalhe = id;
        },
        checkPosition(campo, values = this.selectedPositions) {
            let test = false;
            let numOfItems = values.length / 2;
            let count = 0;
            switch (campo) {
                case 'cliente':
                    values.forEach((sp) => {
                        if (sp.origin == 0) {
                            if (sp.lat == 0 && sp.lng == 0) {
                                count++;
                            }
                        }
                    });
                    if (count == numOfItems) {
                        test = true;
                    }
                    break;
                case 'naovenda':
                    values.forEach((sp) => {
                        if (sp.origin == 1) {
                            if (sp.lat == 0 && sp.lng == 0) {
                                count++;
                            }
                        }
                    });
                    if (count == numOfItems) {
                        test = true;
                    }
                    break;
            }

            return test;
        },
        canShowLine(line) {
            return !(
                (line[0].lat == 0 && line[0].lng == 0) ||
                (line[1].lat == 0 && line[1].lng == 0)
            );
        },
        canShowPin(pin) {
            return !(pin.lat == 0 && pin.lng == 0);
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    min-height: 100%;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;
        min-height: 100%;
        height: inherit;
        .map-marker:hover {
            cursor: pointer;
            opacity: 0.5;
        }
    }

    $width: 400px;
    .filtros-right {
        z-index: 100;
        padding: 15px;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: $darker-bg-color;
        width: $width;
        height: 80%;
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        border-top: solid 1px #d9d9d9;
        border-left: solid 1px #d9d9d9;
        border-bottom: solid 1px #d9d9d9;
        transition: 0.6s;
        -webkit-box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);
        box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);

        .filtros-container {
            margin-bottom: 60px;
        }

        .filtros-footer {
            border-bottom-left-radius: 5px;
            background-color: #fff;
            position: absolute;
            padding: 10px;
            bottom: 0;
            left: 0;
            right: 0;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);
            box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);

            .is-silver {
                color: $semi-title-color;
                box-shadow: inset 0px 0px 2px $dark-gray-color-opacity;
                @include exclude-button();
            }
        }
    }
    .hide-filtros-right {
        margin-right: -$width;
    }
    .mt-2-custom {
        margin-top: 1.5rem;
    }
    @media screen and (min-height: 850px) {
        .filtros-right {
            height: 50%;
        }
    }
    @media screen and (max-width: 640px) {
        .mt-2-custom {
            margin-top: 0;
        }
        $width: 95%;
        .filtros-right {
            width: $width;
        }
        .hide-filtros-right {
            margin-right: -$width;
        }
    }
}
</style>
